import { useCallback } from 'react';

import useAsyncValue from '../../../../../hooks/useAsyncValue';
import useRequestConnectedToAsyncValue from '../../../../../hooks/useRequestConnectedToAsyncValue';
import { get } from '../../../../../redux/api';
import type { ProjectsFilter } from '../../../../../../shared/projectsV2/filters';
import type { MinimalProject, MinimalProjectsResponse } from '../../../../../../shared/projectsV2/response';

const fetchMinimalProjects = (filter: ProjectsFilter) => {
  return get<MinimalProjectsResponse>('/api/v2/projects/minimal', false, filter);
};

/**
 * This hook will provide the callback for fetching minimal projects and the asyncValue,
 * but will also call the provided function to when the minimal projects are fetched,
 * allowing the outer state to be updated.
 *
 * This is useful for both adding the fetched minimal projects to the state, as well
 * as for removing them.
 */
export function useFetchMinimalProjects(updateAddedProjects: (projects: MinimalProject[]) => void) {
  const asyncValue = useAsyncValue<MinimalProjectsResponse | null>(null);
  const fetchMinimalProjectsAndSetState = useCallback(async (filter: ProjectsFilter) => {
    //  Note that we don't need to try/catch this because
    //  that is handled in useRequestConnectedToAsyncValue
    const { data } = await fetchMinimalProjects(filter);

    //  Handle the case where the response is null.
    if (!data) {
      return {
        data: null,
      };
    }
    //  Update the state (either adding or removing these MinimalProjects we just fetched)
    updateAddedProjects(data.projects);

    //  This is so that if we later wanted to await the response directly, we could.
    return { data };
  }, [updateAddedProjects]);

  const onFetchMinimalProjects = useRequestConnectedToAsyncValue(
    fetchMinimalProjectsAndSetState,
    asyncValue,
  ) as unknown as typeof fetchMinimalProjectsAndSetState;

  //  TODO: we should use the asyncValue and isLoading/errors in the UI.
  return onFetchMinimalProjects;
}

/**
 * Fetches minimal projects and updates the state.
 * Provides callbacks which will fetch the minimal projects and
 * update the `addedProjects` part of the state accordingly.
 */
export function useFetchMinimalProjectsAndUpdateState(
  addMinimalProjects: (projects: MinimalProject[]) => void,
  removeProjects: (projects: MinimalProject[]) => void,
  filters: ProjectsFilter,
) {
  //  This is a function which will take the current filters, fetch the minimal projects, and add them to addedProjects.
  const onFetchMinimalProjectsAndAdd = useFetchMinimalProjects(addMinimalProjects);
  //  This is a function which will take the current filters, fetch the minimal projects, and remove them from addedProjects.
  const onFetchMinimalProjectsAndRemove = useFetchMinimalProjects(removeProjects);

  /* eslint-disable react-hooks/exhaustive-deps */
  const onAddAllProjects = useCallback(() => onFetchMinimalProjectsAndAdd(filters), [filters]);
  const onRemoveAllProjects = useCallback(() => onFetchMinimalProjectsAndRemove(filters), [filters]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return {
    onFetchMinimalProjectsAndAdd,
    onAddAllProjects,
    onRemoveAllProjects,
  };
}

export default useFetchMinimalProjectsAndUpdateState;
